'use client';

/**
 * GDPR / ePrivacy compliant cookie consent banner.
 *
 * Behaviour:
 *  - First visit: banner appears at the bottom; no non-essential cookies set
 *  - User picks: "Accept all" / "Reject all" / "Customise"
 *  - Customise opens granular toggles for: Essential / Analytics / Functional
 *  - Choice is persisted in localStorage as `mcc-consent-v1` JSON
 *  - Choice expires after 12 months (re-prompt thereafter)
 *  - "Cookie preferences" link in the footer re-opens the banner
 *
 * The banner does NOT load any third-party scripts itself — it only writes
 * the consent state. Other code (e.g. analytics initialisers) should read
 * `window.__mccConsent` and only fire if the relevant category is true.
 */

import * as React from 'react';

type Category = 'essential' | 'analytics' | 'functional';

interface ConsentState {
  version: 1;
  decidedAt: string; // ISO
  essential: true;   // always true
  analytics: boolean;
  functional: boolean;
}

const STORAGE_KEY = 'mcc-consent-v1';
const CONSENT_TTL_DAYS = 365;

declare global {
  interface Window {
    __mccConsent?: ConsentState;
    __mccOpenCookiePrefs?: () => void;
  }
}

function readConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== 1) return null;
    const ageMs = Date.now() - new Date(parsed.decidedAt).getTime();
    if (ageMs > CONSENT_TTL_DAYS * 86_400_000) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(c: ConsentState) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    window.__mccConsent = c;
    window.dispatchEvent(new CustomEvent('mcc-consent-changed', { detail: c }));
  } catch { /* localStorage not available */ }
}

export function CookieBanner() {
  const [open, setOpen] = React.useState(false);
  const [showCustomise, setShowCustomise] = React.useState(false);
  const [analytics, setAnalytics]   = React.useState(false);
  const [functional, setFunctional] = React.useState(false);

  // First-mount: decide whether to show
  React.useEffect(() => {
    const existing = readConsent();
    if (existing) {
      window.__mccConsent = existing;
    } else {
      setOpen(true);
    }
    // Expose a global so the footer link can re-open the banner
    window.__mccOpenCookiePrefs = () => {
      const cur = readConsent();
      if (cur) {
        setAnalytics(cur.analytics);
        setFunctional(cur.functional);
      }
      setShowCustomise(true);
      setOpen(true);
    };
    return () => { delete window.__mccOpenCookiePrefs; };
  }, []);

  function decide(opts: { analytics: boolean; functional: boolean }) {
    const c: ConsentState = {
      version: 1,
      decidedAt: new Date().toISOString(),
      essential: true,
      analytics: opts.analytics,
      functional: opts.functional,
    };
    writeConsent(c);
    setOpen(false);
    setShowCustomise(false);
  }

  if (!open) return null;

  return (
    <>
      <div
        role="dialog"
        aria-label="Cookie preferences"
        aria-modal="false"
        style={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          maxWidth: 720,
          margin: '0 auto',
          background: 'var(--mcc-deep)',
          color: 'white',
          borderRadius: 12,
          boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          fontSize: 14,
          lineHeight: 1.55,
          padding: '20px 24px',
          zIndex: 9999,
          border: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        {!showCustomise ? (
          <>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>Cookies on marineconservationcredits.com</h2>
            <p style={{ margin: '0 0 16px', color: 'rgba(255,255,255,0.85)' }}>
              We use essential cookies so the registry, marketplace and portal work. With your consent we may also use analytics cookies (to understand which pages help us improve) and functional cookies (to remember preferences). Read details in our <a href="/privacy" style={{ color: '#7ad6ff', textDecoration: 'underline' }}>Privacy Policy</a>.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCustomise(true)} style={btnGhost}>Customise</button>
              <button onClick={() => decide({ analytics: false, functional: false })} style={btnSecondary}>Reject non-essential</button>
              <button onClick={() => decide({ analytics: true, functional: true })} style={btnPrimary}>Accept all</button>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 6px' }}>Cookie preferences</h2>
            <p style={{ margin: '0 0 16px', color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
              Choose which categories of cookies are stored on your device. Essential cookies are always on because the platform cannot operate without them.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              <CategoryRow
                title="Essential"
                desc="Authentication, session, security, registry integrity. Required to log in or use the marketplace."
                checked={true}
                disabled
              />
              <CategoryRow
                title="Analytics"
                desc="Aggregate usage statistics so we can understand which pages help institutional users (no individual profiling)."
                checked={analytics}
                onChange={setAnalytics}
              />
              <CategoryRow
                title="Functional"
                desc="Remembering portal preferences, table sort order, expanded sections."
                checked={functional}
                onChange={setFunctional}
              />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => decide({ analytics: false, functional: false })} style={btnSecondary}>Reject non-essential</button>
              <button onClick={() => decide({ analytics, functional })} style={btnPrimary}>Save preferences</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function CategoryRow({
  title,
  desc,
  checked,
  onChange,
  disabled,
}: {
  title: string;
  desc: string;
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: disabled ? 'default' : 'pointer', padding: '8px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        style={{ marginTop: 2, accentColor: 'var(--mcc-sea)', width: 16, height: 16 }}
        aria-label={title}
      />
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontWeight: 600, fontSize: 13.5 }}>{title}{disabled ? ' (always on)' : ''}</span>
        <span style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{desc}</span>
      </span>
    </label>
  );
}

const btnPrimary: React.CSSProperties = {
  background: 'var(--mcc-sea)',
  color: 'white',
  border: 'none',
  padding: '10px 18px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
const btnSecondary: React.CSSProperties = {
  background: 'rgba(255,255,255,0.12)',
  color: 'white',
  border: '1px solid rgba(255,255,255,0.25)',
  padding: '10px 18px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  background: 'transparent',
  color: 'rgba(255,255,255,0.85)',
  border: '1px solid rgba(255,255,255,0.25)',
  padding: '10px 18px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};
